import React, {
  ReactElement,
  useState,
  useEffect,
  useCallback,
} from 'react';
import ReactDOM from 'react-dom';
import {
  BrowserRouter,
  Route,
  RouteComponentProps,
} from 'react-router-dom';
import { connectHits } from 'react-instantsearch-dom';
import axios from 'axios';
import DatePicker from 'react-date-picker';

import { isEqual } from 'lodash';
import Select from 'react-select';
import Form from 'react-bootstrap/Form';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import {
  LITTERS_ADMIN_PREFIX,
  ALGOLIA_LITTERS_INDEX,
  SELLER_PREFIX,
} from '../../../utils/constants';
import DateHelper from '../../../utils/DateHelper';
import AdminURLSync from '../AdminURLSync';
import useBulkSelected from '../../../utils/hooks/useBulkSelected';
import useModal from '../../../utils/hooks/useModal';
import ConfirmationModal from '../widgets/ConfirmationModal';
import DateFilterDisplay from '../widgets/filters/display-components/DateFilterDisplay';
import BreedFilterDisplay from '../widgets/filters/display-components/BreedFilterDisplay';
import UserFilterDisplay from '../widgets/filters/display-components/UserFilterDisplay';
import EditSVG from './assets/EditSVG';
import {
  AlgoliaSimpleFacetOptions,
  LitterInterface,
  SearchState,
} from './interfaces';
import AdminDashboardTableHits from './AdminDashboardTableHits';
import AdminDashboardTableHit from './AdminDashboardTableHit';

const Hit: React.FC<LitterInterface> = ({
  id,
  admin_breed_display,
  route,
  created_at,
  seller_name,
  seller_slug,
  listings_type,
  selected,
  toggleSelected,
}) => {
  const [isHidden, setIsHidden] = useState(false);

  const {
    modalData,
    setModalData,
    showModal,
    setShowModal,
  } = useModal();

  const updateModal = (type: string) => {
    switch (type) {
      case 'delete':
        setModalData({
          message: `Deleting litter with id ${id} will delete any attached listings, are you sure you want to continue?`,
          onConfirmAsync: () => {
            return new Promise((res, rej) => {
              axios.delete(`${LITTERS_ADMIN_PREFIX}/${id}`)
                .then(({ data }) => {
                  res(data.message);
                })
                .catch((e) => {
                  // console.log(e);
                  rej(e);
                });
            });
          },
          onClose: () => {
            updateModal('');
          },
          onSuccess: () => {
            setIsHidden(true);
          },
        });
        break;
      default:
        setModalData({
          message: '',
          onClose: () => {},
        });
    }
  };

  // TODO: update `any`
  const values: any = [
    {
      key: 'edit-mobile',
      wrapperClass: 'd-xl-none',
      value: (
        <a
          className="edit btn btn-admin-button-gray d-flex align-items-center"
          href={`${LITTERS_ADMIN_PREFIX}/${id}/edit`}
        >
          <EditSVG />
        </a>
      ),
    },
    {
      key: 'delete-mobile',
      wrapperClass: 'd-xl-none',
      value: (
        <a
          href="#"
          className="mx-2"
          aria-label={`Delete litter for: ${seller_name}`}
          onClick={(e) => {
            e.preventDefault();
            updateModal('delete');
          }}
        >
          <FontAwesomeIcon icon={faTrashAlt} color="red" size="xs" />
        </a>
      ),
    },
    {
      key: 'title',
      value: (
        <a
          className="d-flex align-items-center"
          href={route}
        >
          <p className="m-0 text-dark-btn-txt font-weight-bold">{id}</p>
        </a>
      ),
    },
    {
      key: 'admin_breed_display',
      value: <p className="text-black m-0">{admin_breed_display}</p>,
    },
    {
      key: 'seller_profile',
      value: (
        <a
          className="text-table-light-gray m-0"
          href={`${SELLER_PREFIX}${seller_slug}`}
        >
          {seller_name}
        </a>
      ),
    },
    {
      key: 'created_at',
      value: (
        <p className="text-table-light-gray m-0">
          {DateHelper.getLocalizedDateTimeFromTimestamp(created_at).format('MM/DD/YY')}
        </p>
      ),
    },
    {
      key: 'listings_type',
      value: <p className="text-table-light-gray m-0 text-capitalize">{listings_type}</p>,
    },
    {
      key: 'edit',
      wrapperClass: 'd-none d-xl-table-cell',
      value: (
        <a
          className="edit widescreen btn btn-admin-button-gray d-flex align-items-center"
          href={`${LITTERS_ADMIN_PREFIX}/${id}/edit`}
        >
          <EditSVG />
          <span className="ml-1">EDIT</span>
        </a>
      ),
    },
    {
      key: 'delete',
      wrapperClass: 'd-none d-xl-table-cell',
      value: (
        <a
          href="#"
          className="widescreen mx-2"
          aria-label={`Delete litter for: ${seller_name}`}
          onClick={(e) => {
            e.preventDefault();
            updateModal('delete');
          }}
        >
          <FontAwesomeIcon icon={faTrashAlt} color="red" size="xs" />
        </a>
      ),
    },
  ];

  return (
    <>
      {!isHidden && (
        <AdminDashboardTableHit
          values={values}
          id={id}
          selected={selected}
          toggleSelected={toggleSelected}
        />
      )}
      {!isHidden && (
        <ConfirmationModal
          show={showModal}
          setShow={setShowModal}
          {...modalData}
        />
      )}
    </>
  );
};

const Hits: React.FC<{
  hits: any,
  selected: Array<number>,
  toggleSelected: (id: number) => void,
}> = ({
  hits,
  selected,
  toggleSelected,
}) => {
  return (
    <AdminDashboardTableHits
      hits={hits}
      Hit={Hit}
      selected={selected}
      toggleSelected={toggleSelected}
    />
  );
};

const CustomHits = connectHits(Hits);

type Props = RouteComponentProps & AlgoliaSimpleFacetOptions;

const AdminLitters: React.FC<Props> = ({
  history,
  location,
  match,
  facets,
}): ReactElement => {
  const getAdType = (browserLocation) => {
    const { search } = browserLocation;
    const query = new URLSearchParams(search);
    return query.get('type') || 'all'; // Default to 'all'
  };

  const loadInitialSearchState = (adType) => {
    const facetFilters = (adType === 'puppy' || adType === 'stud')
      ? [`listings_type:${adType}`]
      : [];

    return {
      configure: {
        numericFilters: [],
        facetFilters,
      },
      query: '',
    };
  };

  const getBreedOtherUnknown = (): string => {
    const breedsEl = document.getElementById('react-breeds-data');
    if (breedsEl) {
      return breedsEl.getAttribute('data-breed-other-unknown') || '';
    }
    return '';
  };

  const [adType, setAdType] = useState(getAdType(location));
  const [externalSearchState, setExternalSearchState] = useState<SearchState>(
    loadInitialSearchState(adType),
  );
  const [breeds, setBreeds] = useState<{id: number, name: string}[]>([]);
  const [breedOtherUnknown] = useState(getBreedOtherUnknown());
  const [breedMax, setBreedMax] = useState<number|null>(null);

  const {
    bulkAction,
    setBulkAction,
    updateBulkSelected,
    bulkSelected,
    toggleAllSelected,
    allSelected,
    setHits,
    clearBulkSelected,
  } = useBulkSelected();

  const {
    modalData,
    setModalData,
    showModal,
    setShowModal,
    modalFormState,
    setModalFormState,
    reloadPage,
  } = useModal();

  const performBulkAction = useCallback((action: string) => {
    if (bulkSelected.length < 1) {
      setModalData({
        message: 'No litters have been selected. Please, select one or more litters to bulk edit.',
        isSimple: true,
        onConfirm: () => {
          setModalData({
            message: '',
            onClose: () => {},
            isSimple: true,
          });
        },
        onClose: () => {
          setModalData({
            message: '',
            onClose: () => {},
            isSimple: true,
          });
        },
      });
    } else {
      switch (action) {
        case 'set_birthdate':
          setModalData({
            message: `Would you like to update the 'Birthdate' of ${bulkSelected.length} litters?`,
            ModalForm: () => {
              const [date, setDate] = useState(new Date());
              useEffect(() => {
                if (date) {
                  setModalFormState({
                    birth_date: moment.utc(date),
                  });
                }
              }, [date]);
              return (
                <div>
                  <Form>
                    <DatePicker
                      onChange={(val) => setDate(val)}
                      value={date}
                      maxDate={new Date()}
                      // showDisabledMonthNavigation={true}
                    />
                  </Form>
                </div>
              );
            },
            onConfirmAsync: (fields) => {
              return new Promise((res, rej) => {
                axios.patch(
                  `${LITTERS_ADMIN_PREFIX}/${bulkSelected.join(',')}`,
                  fields,
                )
                  .then(({ data }) => {
                    res(data.message);
                  })
                  .catch((e) => {
                    // console.log(e);
                    rej(e);
                  });
              });
            },
            onClose: () => {
              performBulkAction('');
              setModalFormState({});
            },
            onSuccess: () => reloadPage(),
          });
          break;
        case 'set_breed':
          setModalData({
            message: `Would you like to update the 'Breed(s)' of ${bulkSelected.length} litters?`,
            ModalForm: () => {
              const [breedID, setBreedID] = useState<string|number|undefined>(undefined);
              const [mixBreedID, setMixBreedID] = useState<string|number|undefined>(undefined);
              useEffect(() => {
                const newBreeds = {} as {
                  breed_id?: string|number|undefined,
                  mix_breed_id?: string|number|undefined,
                };
                if (breedID) {
                  newBreeds.breed_id = breedID;
                }
                if (mixBreedID) {
                  newBreeds.mix_breed_id = mixBreedID;
                }
                setModalFormState(newBreeds);
              }, [breedID, mixBreedID]);
              return (
                <div>
                  <Form>
                    <div>
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label
                        aria-hidden={true}
                        className="font-weight-bold"
                      >
                        Breed
                      </label>
                      <Select
                        placeholder="Choose a breed..."
                        options={breeds
                          .filter((breed) => breed.name !== breedOtherUnknown)
                          .map((breed) => ({
                            label: breed.name,
                            value: breed.id,
                          }))}
                        onChange={(e) => setBreedID(e?.value)}
                        isClearable={true}
                      />
                    </div>
                    <div>
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label
                        aria-hidden={true}
                        className="font-weight-bold"
                      >
                        Mix Breed
                      </label>
                      <Select
                        placeholder="Choose a mix breed..."
                        options={breeds.map((breed) => ({
                          label: breed.name,
                          value: breed.id,
                        }))}
                        onChange={(e) => setMixBreedID(e?.value)}
                        isClearable={true}
                      />
                    </div>
                  </Form>
                </div>
              );
            },
            onConfirmAsync: (fields) => {
              return new Promise((res, rej) => {
                axios.patch(
                  `${LITTERS_ADMIN_PREFIX}/${bulkSelected.join(',')}`,
                  fields,
                )
                  .then(({ data }) => {
                    res(data.message);
                  })
                  .catch((e) => {
                    // console.log(e);
                    rej(e);
                  });
              });
            },
            onClose: () => {
              performBulkAction('');
              setModalFormState({});
            },
            onSuccess: () => reloadPage(),
          });
          break;
        case 'delete':
          setModalData({
            message: `Would you like to delete ${bulkSelected.length} litters?`,
            onConfirmAsync: () => {
              return new Promise((res, rej) => {
                axios.delete(`${LITTERS_ADMIN_PREFIX}/${bulkSelected.join(',')}`)
                  .then(({ data }) => {
                    res(data.message);
                  })
                  .catch((e) => {
                    // console.log(e);
                    rej(e);
                  });
              });
            },
            onClose: () => {
              performBulkAction('');
              setModalFormState({});
            },
            onSuccess: () => reloadPage(),
          });
          break;
        default:
          setModalData({
            message: '',
            onClose: () => {},
            isSimple: true,
          });
      }
    }
  }, [bulkSelected, modalFormState, breeds, breedOtherUnknown, reloadPage]);

  useEffect(() => {
    const { value } = bulkAction;
    if (value) {
      performBulkAction(value);
    }
  }, [bulkAction]);

  useEffect(() => {
    const type = getAdType(location);

    if (type !== adType) {
      setAdType(type);
    }
  }, [location]);

  useEffect(() => {
    if (adType === 'all') {
      const { configure } = externalSearchState;
      if (configure) {
        const { facetFilters } = configure;
        if (!isEqual(facetFilters, [])) {
          setExternalSearchState({
            ...externalSearchState,
            configure: {
              ...externalSearchState.configure,
              facetFilters: [],
            },
          });
        }
      }
    } else if (adType === 'puppy') {
      const { configure } = externalSearchState;
      if (configure) {
        const { facetFilters } = configure;
        if (!isEqual(facetFilters, ['listings_type:puppy'])) {
          setExternalSearchState({
            ...externalSearchState,
            configure: {
              ...externalSearchState.configure,
              facetFilters: ['listings_type:puppy'],
            },
          });
        }
      }
    } else if (adType === 'stud') {
      const { configure } = externalSearchState;
      if (configure) {
        const { facetFilters } = configure;
        if (!isEqual(facetFilters, ['listings_type:stud'])) {
          setExternalSearchState({
            ...externalSearchState,
            configure: {
              ...externalSearchState.configure,
              facetFilters: ['listings_type:stud'],
            },
          });
        }
      }
    }
  }, [adType]);

  useEffect(() => {
    const breedsEl = document.getElementById('react-breeds-data');
    if (breedsEl) {
      const newBreeds = breedsEl.dataset.breeds
        ? JSON.parse(breedsEl.dataset.breeds)
        : [];
      setBreeds(newBreeds);
    }
  }, []);

  const tableHeaderRowValues = [
    {
      key: 'id',
      value: 'Id',
    },
    {
      key: 'breed',
      value: 'Breed',
      sort: {
        asc: `${ALGOLIA_LITTERS_INDEX}_breed_asc`,
      },
    },
    {
      key: 'seller',
      value: 'Seller Profile',
    },
    {
      key: 'posted',
      value: 'Posted',
      sort: {
        desc: `${ALGOLIA_LITTERS_INDEX}`,
      },
    },
    {
      key: 'listings_type',
      value: 'Type',
    },
  ];

  const singleBreedCallback = useCallback((checked: boolean) => {
    if (!checked && breedMax !== 2) {
      setBreedMax(2);
    } else if (breedMax) {
      setBreedMax(null);
    }
  }, [breedMax]);

  return (
    <>
      <AdminURLSync
        title={`${adType !== 'all' ? adType : ''} Litters`}
        numEmptyStartHeaders={2}
        numEmptyEndHeaders={2}
        setBulkAction={setBulkAction}
        bulkAction={bulkAction}
        setHits={setHits}
        allSelected={allSelected}
        toggleAllSelected={toggleAllSelected}
        clearBulkSelected={clearBulkSelected}
        bulkActions={[
          {
            label: 'Update Breed(s)',
            value: 'set_breed',
          },
          {
            label: 'Set Birthdate',
            value: 'set_birthdate',
          },
          {
            label: 'Flag For Review',
            value: 'flag',
          },
          {
            label: 'Delete',
            value: 'delete',
          },
        ]}
        externalQueryParams={[
          {
            key: 'type',
            value: adType,
          },
        ]}
        history={history}
        location={location}
        match={match}
        externalSearchState={externalSearchState}
        tableHeaderRowValues={tableHeaderRowValues}
        indexName={ALGOLIA_LITTERS_INDEX!}
        customHits={(
          <CustomHits
            selected={bulkSelected}
            toggleSelected={updateBulkSelected}
          />
        )}
        attributes={[
          {
            type: 'dateRange',
            attribute: 'created_at',
            startLabel: '',
            endLabel: '',
            DisplayComponent: <DateFilterDisplay />,
          },
          {
            type: 'facet',
            attribute: 'admin_breed_parents',
            isSearchable: true,
            isAsync: true,
            isMulti: true,
            max: breedMax,
            andSearch: breedMax === 2,
            DisplayComponent: <BreedFilterDisplay />,
            placeholder: '',
            simpleOptions: facets
              ? facets[`admin_breed_parents${adType === 'puppy' || adType === 'stud' ? `_${adType}` : ''}`] || []
              : [],
          },
          {
            type: 'facet_switch',
            attribute: 'admin_single_breed',
            label: 'Single Breeds Only',
            toggleCallback: singleBreedCallback,
          },
          {
            type: 'facet',
            attribute: 'seller_name',
            label: 'Seller Profile',
            isSearchable: true,
            isAsync: true,
            isMulti: true,
            isToggleable: true,
            DisplayComponent: <UserFilterDisplay title="Seller Profile:" />,
            placeholder: '',
          },
          {
            type: 'facet',
            attribute: 'seller_id',
            label: 'Seller ID',
            isSearchable: true,
            isAsync: true,
            isMulti: true,
            isToggleable: true,
            DisplayComponent: <UserFilterDisplay title="Breeder ID:" />,
            placeholder: '',
          },
          {
            type: 'facet',
            attribute: 'seller.user.username',
            label: 'Account Username',
            isSearchable: true,
            isAsync: true,
            isMulti: true,
            isToggleable: true,
            DisplayComponent: <UserFilterDisplay title="Account Username:" />,
            placeholder: '',
          },
        ]}
        dateAttributes={[]}
        tabs={[
          {
            title: 'All Litters',
            route: `${LITTERS_ADMIN_PREFIX}?type=all`,
            key: 'type=all',
            indexName: ALGOLIA_LITTERS_INDEX,
            facetFilters: [],
            numericFilters: [],
          },
          {
            title: 'Puppy Litters',
            route: `${LITTERS_ADMIN_PREFIX}?type=puppy`,
            key: 'type=puppy',
            indexName: ALGOLIA_LITTERS_INDEX,
            facetFilters: ['listings_type:puppy'],
            numericFilters: [],
          },
          {
            title: 'Stud Litters',
            route: `${LITTERS_ADMIN_PREFIX}?type=stud`,
            key: 'type=stud',
            indexName: ALGOLIA_LITTERS_INDEX,
            facetFilters: ['listings_type:stud'],
            numericFilters: [],
          },
        ]}
      />
      <ConfirmationModal
        show={showModal}
        setShow={setShowModal}
        modalFormState={modalFormState}
        clearBulkSelected={clearBulkSelected}
        {...modalData}
      />
    </>
  );
};

export default AdminLitters;

if (document.getElementById('react-admin-dashboard-table-litters-puppy')
  || document.getElementById('react-admin-dashboard-table-litters-stud')
  || document.getElementById('react-admin-dashboard-table-litters-all')) {
  const element = document.getElementById('react-admin-dashboard-table-litters-puppy')
    || document.getElementById('react-admin-dashboard-table-litters-stud')
    || document.getElementById('react-admin-dashboard-table-litters-all');

  const facets = element?.dataset.facets && JSON.parse(element?.dataset.facets);

  ReactDOM.render(
    <BrowserRouter>
      <Route
        path={LITTERS_ADMIN_PREFIX}
        render={(routeProps) => (
          <AdminLitters
            facets={facets}
            {...routeProps}
          />
        )}
      />
    </BrowserRouter>,
    element,
  );
}
